import { useState } from "react";
import { api, errorText } from "../api";
import { playLocal } from "../audio";
import { promptDialog } from "../dialog";
import { toast } from "../toast";
import type { Me, Preset, Row } from "../types";
import { EmptyState } from "./EmptyState";
import { padName, unstarRow } from "./Feed";
import { Icons } from "./Icons";
import { ActionSheet } from "./Sheet";

/* Starred clips as buttons.

   A pad is a clip that already exists, so pressing one is a replay: it costs no
   credits and needs no generation, which is the point of having one for "thanks
   for the sub" instead of typing it for the fortieth time. Anyone may press a
   pad — the replay route still applies the cooldown to non-admins — and only an
   admin can make, rename or remove them. */

const fail = (error: unknown) => toast(errorText(error), "error");

async function rename(row: Row) {
  const label = await promptDialog({
    title: "Rename clip",
    body: `“${row.text}”`,
    confirmLabel: "Rename",
    prompt: { label: "Name", initial: row.label ?? "", maxLength: 32 },
  });
  if (label === null) return;
  await api(`/api/requests/${row.id}/star`, {
    method: "POST",
    body: JSON.stringify({ starred: true, label }),
  }).catch(fail);
}

function Pad({ row, me, presetName }: { row: Row; me: Me; presetName: string | null }) {
  const [fired, setFired] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);

  function fire() {
    setFired((count) => count + 1);
    void api(`/api/requests/${row.id}/replay`, { method: "POST" }).catch(fail);
  }

  return (
    <div className="pad-wrap">
      <button
        type="button"
        // The key restarts the flash animation on every press, even a fast
        // second one, so the button always answers the click it was given.
        key={fired}
        className={"pad" + (fired ? " fired" : "")}
        title={row.text}
        onClick={fire}
      >
        <span className="pad-name">{padName(row)}</span>
        <span className="pad-meta">{presetName ?? row.voice_name}</span>
      </button>
      <button
        type="button"
        className="pad-more icon-btn"
        aria-label={`More for “${padName(row)}”`}
        aria-haspopup="dialog"
        onClick={() => setSheetOpen(true)}
      >
        {Icons.more}
      </button>
      <ActionSheet
        open={sheetOpen}
        title={padName(row)}
        onClose={() => setSheetOpen(false)}
        actions={[
          {
            label: "Listen here",

            run: () => row.audio_url && playLocal(row.audio_url),
          },
          ...(me.admin
            ? [
                { label: "Rename", run: () => void rename(row) },
                {
                  label: "Remove from the soundboard",

                  danger: true,
                  run: () => unstarRow(row),
                },
              ]
            : []),
        ]}
      />
    </div>
  );
}

export function Soundboard({
  pads,
  me,
  presets,
  compact = false,
}: {
  pads: Row[];
  me: Me;
  presets: Preset[];
  compact?: boolean;
}) {
  const nameOf = (row: Row) =>
    row.preset === null ? null : (presets.find((preset) => preset.id === row.preset)?.name ?? null);

  return (
    <div className={"card soundboard" + (compact ? " compact" : "")}>
      <h2>
        Soundboard <span className="spacer"></span>
        <span className="muted">{pads.length ? `${pads.length} clips` : ""}</span>
      </h2>
      {pads.length === 0 ? (
        <EmptyState
          art="soundboard"
          compact={compact}
          title="No clips yet"
          body={me.admin ? "Star a message in the history to add it here." : undefined}
        />
      ) : (
        <div className="pads">
          {pads.map((row) => (
            <Pad key={row.id} row={row} me={me} presetName={nameOf(row)} />
          ))}
        </div>
      )}
    </div>
  );
}
